import template from './template';
import styles from './template.css';
import {AoflElement, customElement, property} from '@aofl/element';
import {validationMixin, isRequired} from '@aofl/form-validate';
import {isNotEmoji, isNotZero, isNotExpired} from '@aoflmkt/form-validators';
import {paymentFormSdo} from './sdo';

/**
 *
 * @summary Base payment form used in marketing subscription pages.
 * @extends {AoflElement}
 */
@customElement('payment-form')
class PaymentForm extends validationMixin(AoflElement) {
  /**
   *
   * @readonly
   */
  static is = 'payment-form';

  @property({type: String, attribute: false, store: paymentFormSdo, mapState: 'cardName'})
  cardName = '';

  @property({type: String, attribute: false, store: paymentFormSdo, mapState: 'cardNumber'})
  cardNumber = '';

  @property({type: String, attribute: false, store: paymentFormSdo, mapState: 'cardMonth'})
  cardMonth = ('0' + (new Date().getMonth() + 1)).slice(-2);

  @property({type: String, attribute: false, store: paymentFormSdo, mapState: 'cardYear'})
  cardYear = new Date().getFullYear().toString();

  @property({type: String, attribute: false, store: paymentFormSdo, mapState: 'cardCvv'})
  cardCvv = '';

  @property({type: String, attribute: false, store: paymentFormSdo, mapState: 'zipCode'})
  zipCode = '';

  @property({type: String, attribute: false, store: paymentFormSdo, mapState: 'phoneNumber'})
  phoneNumber = '';

  @property({type: Boolean, attribute: false, store: paymentFormSdo, mapState: 'monthDropdownActive'})
  monthDropdownActive = '';

  @property({type: Boolean, attribute: false, store: paymentFormSdo, mapState: 'yearDropdownActive'})
  yearDropdownActive = '';

  @property({type: Array, attribute: false})
  months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

  @property({type: Array, attribute: false})
  years = [];

  /**
   *
   * @return {Object}
   */
  defineValidators() {
    return {
      cardName: {isRequired, isNotEmoji},
      cardNumber: {isRequired, isNotZero},
      cardMonth: {isRequired},
      cardYear: {isRequired},
      cardCvv: {isRequired, isNotZero, isNotEmoji},
      expDate: {isNotExpired: () => isNotExpired(paymentFormSdo.expDate)},
      zipCode: {isRequired, isNotZero, isNotEmoji}
    };
  }

  /**
   * Implement this at the application level
   */
  showCvvModal() {

  }

  /**
   *
   * @param {Event} e
   */
  submitForm(e) {
    e.preventDefault();
    this.dispatchEvent(new CustomEvent('paymentFormSubmission'));
  }

  /**
   *
   * @param {Object} e
   */
  updateCardName(e) {
    paymentFormSdo.cardName = e.target.value;
  }

  /**
   *
   */
  validateCardName() {
    this.form.cardName.validate();
  }

  /**
   *
   * @param {Object} e
   */
  updateCardNumber(e) {
    paymentFormSdo.cardNumber = e.target.value;
  }

  /**
   *
   */
  validateCardNumber() {
    this.form.cardNumber.validate();
  }

  /**
   *
   * @param {Object} e
   */
  updateCardMonth(e) {
    paymentFormSdo.cardMonth = e.target.value;
    this.form.expDate.validate();
  }

  /**
   *
   */
  validateCardMonth() {
    this.form.cardMonth.validate();
  }

  /**
   *
   * @param {Object} e
   */
  updateCardYear(e) {
    paymentFormSdo.cardYear = e.target.value;
    this.form.expDate.validate();
  }

  /**
   *
   */
  validateCardYear() {
    this.form.cardYear.validate();
  }

  /**
   *
   * @param {Object} e
   */
  updateCardCvv(e) {
    paymentFormSdo.cardCvv = e.target.value;
  }

  /**
   *
   */
  validateCardCvv() {
    this.form.cardCvv.validate();
  }

  /**
   *
   * @param {Object} e
   */
  updateZipCode(e) {
    paymentFormSdo.zipCode = e.target.value;
  }

  /**
   *
   */
  validateZipCode() {
    this.form.zipCode.validate();
  }

  /**
   *
   * @param {Object} e
   */
  updatePhoneNumber(e) {
    paymentFormSdo.phoneNumber = e.target.value;
  }

  /**
   * @return {Array}
   */
  getYears() {
    const defaultYearRange = 11;
    const start = new Date().getFullYear();
    const end = start + defaultYearRange;
    const years = [];

    for (let i = start; i <= end; i++) {
      years.push(i.toString());
    }

    return years;
  }

  /**
   *
   */
  documentClickHandler() {
    paymentFormSdo.yearDropdownActive = false;
    paymentFormSdo.monthDropdownActive = false;
  }

  /**
   * @param {Event} e
   */
  toggleMonth(e) {
    e.stopPropagation();
    const currentState = paymentFormSdo.monthDropdownActive;
    paymentFormSdo.monthDropdownActive = !currentState;
    paymentFormSdo.yearDropdownActive = false;
  }

  /**
   * @param {Event} e
   */
  toggleYear(e) {
    e.stopPropagation();
    const currentState = paymentFormSdo.yearDropdownActive;
    paymentFormSdo.yearDropdownActive = !currentState;
    paymentFormSdo.monthDropdownActive = false;
  }

  /**
   *
   */
  connectedCallback() {
    super.connectedCallback();
    this.years = this.getYears();
    this.validators = this.defineValidators();
    document.addEventListener('click', () => {
      this.documentClickHandler();
    });
  }

  /**
   * @param {Object} childTemplate - template to override default template
   * @param {Object} childStyles - styles for form
   * @return {Object}
   */
  render(childTemplate = template, childStyles = styles) {
    return super.render(childTemplate, ...childStyles);
  }

  /**
   *
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    paymentFormSdo.reset();
  }
}

export {PaymentForm};
